import Box from "@@/elements/Box";
import React, {
  useCallback, useContext, useEffect, useRef, useState,
} from "react";
import ScrollerContext from "../context";
import NavButtonWrapper from "../nav-button/buttonWrapper";
import css from "./index.module.scss";

export type SolidBarProps = {
  sideHeader?: boolean;
};

const SolidBar = ({ sideHeader }: SolidBarProps) => {
  const trackEl = useRef<HTMLDivElement>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  const [left, setLeft] = useState(0);
  const { scrollLeft, scrollingElRef } = useContext(ScrollerContext);

  const recalculate = useCallback(() => {
    const el = scrollingElRef.current;
    if (!el || !trackEl.current) return;
    const trackWidth = trackEl.current.clientWidth;
    const viewPercentage = el.clientWidth / el.scrollWidth;
    const newWidth = Math.max(trackWidth * viewPercentage, 32);
    setWidth(newWidth);
    const maxScroll = el.scrollWidth - el.clientWidth;
    const scrollProgress = maxScroll > 0 ? el.scrollLeft / maxScroll : 0;
    const maxLeft = trackWidth - newWidth;
    setLeft(Math.round(maxLeft * scrollProgress));
  }, []);

  useEffect(() => {
    recalculate();
  }, [scrollLeft]);

  useEffect(() => {
    const el = scrollingElRef.current;
    if (!el) return undefined;
    const ro = new ResizeObserver(recalculate);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const barClick = useCallback((evt: React.MouseEvent) => {
    evt.preventDefault();
    const el = scrollingElRef.current;
    if (!el || !barRef.current || !trackEl.current) return;
    const clickX = evt.pageX - trackEl.current.offsetLeft;
    const clickPercentage = clickX / trackEl.current.clientWidth;
    el.scrollTo({ left: el.scrollWidth * clickPercentage, behavior: "smooth" });
  }, []);

  return (
    <Box
      className="bar-container"
      flexDirection="row"
      width="full"
      pt={6}
      px={sideHeader ? 0 : [0, 0, 25]}
      pr={sideHeader ? [0, 0, 25] : 0}
      flexWrap={["nowrap", "nowrap", "nowrap"]}
      justifyContent="space-between"
      alignItems="center"
      gap={[6, 10, 10]}
    >
      <Box
        container
        width="full"
        className={css["hbd-scroller__solid-bar"]}
        onClick={barClick}
        ref={barRef}
      >
        <Box
          ref={trackEl}
          className={css["hbd-scroller__bar-track"]}
          width="full"
          backgroundColor="PrimaryGreyTint03"
        >
          <div
            className={css["hbd-scroller__bar-current"]}
            style={{
              width,
              transform: `translateX(${left}px)`,
            }}
          />
        </Box>
      </Box>
      <NavButtonWrapper />
    </Box>
  );
};

export default SolidBar;
